import { useState, useMemo, useCallback } from 'react';
import { startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { VoxRoi } from './VoxRoi';
import VoxRoiConfigEditor from './VoxRoiConfigEditor';
import { useVoxRoiConfig, useVoxRoiCatalog, useVoxRoiUsage, useUpdateVoxRoiConfig } from '../../hooks/useVoxRoi';
import { decryptConfigBlob, encryptConfigBlob } from '../../utils/crypto';
import { computeRoi } from '../../utils/voxRoiCalc';
import { VoxRoiCostConfigData, VOX_ROI_CONFIG_VERSION } from '../../models/voxRoi';

type ViewState = 'locked' | 'unlocked' | 'editing';

const defaultDateRange = (): Date[] => {
  const now = new Date();
  return [startOfDay(startOfMonth(now)), endOfDay(endOfMonth(now))];
};

const VoxRoiContainer = () => {
  const [viewState, setViewState] = useState<ViewState>('locked');
  const [passphrase, setPassphrase] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [decryptedConfig, setDecryptedConfig] = useState<VoxRoiCostConfigData | null>(null);
  const [dateRange, setDateRange] = useState<Date[] | undefined>(defaultDateRange);

  const configQuery = useVoxRoiConfig();
  const catalogQuery = useVoxRoiCatalog();
  const updateConfigMutation = useUpdateVoxRoiConfig();

  const startDateIso = dateRange?.[0]?.toISOString();
  const endDateIso = dateRange?.[1]?.toISOString();
  const usageQuery = useVoxRoiUsage(
    viewState === 'unlocked' ? startDateIso : undefined,
    viewState === 'unlocked' ? endDateIso : undefined,
  );

  const configSettled = configQuery.isSuccess || configQuery.isError;
  const hasConfig = !!configQuery.data;

  const handleUnlock = useCallback(async () => {
    setUnlockError(null);
    if (!configSettled) {
      setUnlockError('Still loading configuration, please wait.');
      return;
    }
    if (configQuery.isError) {
      setUnlockError('Failed to load configuration. Please try again.');
      return;
    }
    if (!passphrase.trim()) {
      setUnlockError('Please enter a passphrase.');
      return;
    }

    if (!configQuery.data) {
      const emptyConfig: VoxRoiCostConfigData = {
        version: VOX_ROI_CONFIG_VERSION,
        avgOrdersPerLoan: 1.8,
        productCosts: {},
        beforeSelection: [],
      };
      try {
        const encrypted = await encryptConfigBlob({ plaintext: JSON.stringify(emptyConfig), passphrase });
        await updateConfigMutation.mutateAsync({ encryptedData: encrypted, version: VOX_ROI_CONFIG_VERSION });
        setDecryptedConfig(emptyConfig);
        setViewState('editing');
      } catch {
        setUnlockError('Failed to create configuration.');
      }
      return;
    }

    try {
      const plaintext = await decryptConfigBlob({ blob: configQuery.data.encryptedData, passphrase });
      const parsed = JSON.parse(plaintext) as VoxRoiCostConfigData;
      setDecryptedConfig(parsed);
      setViewState('unlocked');
    } catch {
      setUnlockError('Incorrect passphrase or corrupted data.');
    }
  }, [passphrase, configQuery.data, configQuery.isError, configSettled, updateConfigMutation]);

  const result = useMemo(() => {
    if (!decryptedConfig || !catalogQuery.data || !usageQuery.data) return null;
    return computeRoi({ config: decryptedConfig, catalog: catalogQuery.data, usage: usageQuery.data });
  }, [decryptedConfig, catalogQuery.data, usageQuery.data]);

  const handleSaveConfig = useCallback(async (updatedConfig: VoxRoiCostConfigData) => {
    try {
      const encrypted = await encryptConfigBlob({ plaintext: JSON.stringify(updatedConfig), passphrase });
      await updateConfigMutation.mutateAsync({ encryptedData: encrypted, version: updatedConfig.version });
      setDecryptedConfig(updatedConfig);
      setViewState('unlocked');
    } catch {
      throw new Error('Failed to save configuration.');
    }
  }, [passphrase, updateConfigMutation]);

  if (viewState === 'editing' && decryptedConfig && catalogQuery.data) {
    return (
      <VoxRoiConfigEditor
        config={decryptedConfig}
        catalog={catalogQuery.data}
        onSave={handleSaveConfig}
        onCancel={() => setViewState('unlocked')}
      />
    );
  }

  return (
    <VoxRoi
      status={viewState === 'locked' ? 'locked' : 'unlocked'}
      hasConfig={hasConfig}
      passphrase={passphrase}
      onPassphraseChange={setPassphrase}
      onUnlock={handleUnlock}
      unlockError={unlockError}
      isConfigLoading={!configSettled}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      result={result}
      isUsageLoading={usageQuery.isLoading}
      onEditConfig={() => setViewState('editing')}
    />
  );
};

export default VoxRoiContainer;
